// context/ThemeContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Initialize theme from multiple sources
  const [darkMode, setDarkMode] = useState(() => {
    // 1. Check localStorage first (returning users keep their preference)
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme !== null) {
      try {
        return JSON.parse(savedTheme);
      } catch (e) {
        console.error('Error parsing saved theme:', e);
      }
    }
    
    // 2. Check cookie (for SSR compatibility)
    const cookieTheme = document.cookie
      .split('; ')
      .find(row => row.startsWith('theme='));
    if (cookieTheme) {
      return cookieTheme.split('=')[1] === 'dark';
    }
    
    // 3. Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return true;
    }
    
    // 4. Default to dark mode
    return true;
  });

  // Apply theme to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.className = 'bg-gray-900 min-h-screen transition-colors duration-300';
    } else {
      document.documentElement.classList.remove('dark');
      document.body.className = 'bg-gray-50 min-h-screen transition-colors duration-300';
    }
    
    // Save to localStorage
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
    
    // Save to cookie for SSR and cross-domain compatibility
    document.cookie = `theme=${darkMode ? 'dark' : 'light'}; path=/; max-age=31536000; SameSite=Lax`;
  }, [darkMode]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only auto-update if user hasn't manually set a preference
      const savedTheme = localStorage.getItem('darkMode');
      if (savedTheme === null) {
        setDarkMode(e.matches);
      }
    };
    
    // Check if addEventListener is supported (older Safari)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else if (mediaQuery.addListener) {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  // Sync theme across tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'darkMode' && e.newValue !== null) {
        try {
          setDarkMode(JSON.parse(e.newValue));
        } catch (error) {
          console.error('Error parsing theme from storage:', error);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Enhanced setDarkMode that also updates user preference if logged in
  const updateDarkMode = async (newDarkMode) => {
    setDarkMode(newDarkMode);
    
    // Try to save to server if user is logged in
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        await fetch('/api/users/theme', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ theme: newDarkMode ? 'dark' : 'light' })
        });
      } catch (error) {
        console.warn('Failed to save theme preference to server:', error);
      }
    }
  };

  const value = {
    darkMode,
    setDarkMode: updateDarkMode,
    toggleDarkMode: () => updateDarkMode(!darkMode)
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
